import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { html } from 'lit';
import type { StaticPageAudience } from '../../shared/content/staticPageContent.js';
import type { LandingLocale } from '../../shared/content/landingContent.js';
import { applyCursorEffect, removeCursorEffect } from '../cursor/cursorEffects.js';
import { LandingEditorClient } from '../editor/landingEditorClient.js';
import { syncInlineTextTypography } from '../editor/landingEditorInlineTypography.js';
import { landingProjectContentRepository } from '../editor/landingProjectContent.js';
import { createDefaultLandingEditorProject, getAudienceLandingHomeSections } from '../../shared/editor/landingEditorTypes.js';
import {
  elementLinkClickHandler,
  elementLinkKeyDownHandler,
  syncLinkedElementAccessibility,
  type LandingEditorRenderOptions,
} from '../editor/landingEditorRender.js';
import { footerSection } from '../sections/footerSection.js';
import { renderLandingHomeSection } from '../sections/homeSections.js';
import { landingNavigation } from '../sections/navigation.js';
import { LandingLocaleState } from '../state/landingLocaleState.js';

class HomeAnchorScrollCoordinator {
  private static scheduled = false;

  static scheduleInitialHashScroll() {
    if (typeof window === 'undefined' || this.scheduled || !window.location.hash) return;

    this.scheduled = true;
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        document.querySelector<HTMLElement>(window.location.hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        this.scheduled = false;
      });
    });
  }
}

export const Home = stateful(() => {
  const [locale, setLocale] = useState<LandingLocale>(LandingLocaleState.getInitialLocale());
  const [audience, setAudience] = useState<StaticPageAudience>(
    LandingLocaleState.getAudience(),
    [],
  );
  const [project, setProject] = useState(createDefaultLandingEditorProject(), []);
  const content = landingProjectContentRepository.landing(project, locale);
  const editorRenderOptions: LandingEditorRenderOptions = { project };

  const handleLanguageChange = (nextLocale: LandingLocale) => {
    setLocale(nextLocale);
    LandingLocaleState.change(nextLocale);
  };

  const handleAudienceChange = (nextAudience: StaticPageAudience) => {
    setAudience(nextAudience);
    LandingLocaleState.changeAudience(nextAudience);
  };

  useEffect(() => LandingLocaleState.subscribe(setLocale, setAudience), []);

  useEffect(() => {
    let active = true;
    void LandingEditorClient.loadPublished().then(publishedProject => {
      if (active) setProject(publishedProject);
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const { cursorEffect, cursorColor, cursorCss } = project.settings ?? {};
    if (cursorEffect) {
      applyCursorEffect(cursorEffect, cursorColor ?? '#48c5ce');
    } else if (cursorCss) {
      removeCursorEffect();
      if (typeof document !== 'undefined') document.body.style.cursor = cursorCss;
    } else {
      removeCursorEffect();
    }
    return removeCursorEffect;
  }, [project]);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      syncLinkedElementAccessibility(project);
      syncInlineTextTypography(project, locale);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [project, locale, audience]);

  LandingLocaleState.persist(locale, project.settings?.seoDescription);
  HomeAnchorScrollCoordinator.scheduleInitialHashScroll();

  return html`
    <div
      class="vp-page"
      @click=${elementLinkClickHandler(editorRenderOptions)}
      @keydown=${elementLinkKeyDownHandler(editorRenderOptions)}
    >
      <a class="vp-skip-link" href="#main-content">
        ${locale === 'tr' ? 'İçeriğe geç' : locale === 'en' ? 'Skip to content' : 'Zum Inhalt springen'}
      </a>
      ${landingNavigation({
        content,
        currentLocale: locale,
        onLanguageChange: handleLanguageChange,
        audience,
        onAudienceChange: handleAudienceChange,
        editor: editorRenderOptions,
      })}
      <main id="main-content" tabindex="-1">
        ${getAudienceLandingHomeSections(project, audience).map(section => renderLandingHomeSection(section, content, editorRenderOptions))}
      </main>
      ${footerSection(content, editorRenderOptions)}
    </div>
  `;
});
